import { useCallback, useEffect, useRef, useState } from 'react'
import {
  cleanupExpiredAbandonedCartFlags,
  getAbandonedCartCookie,
  getFreeShippingIntentSessionFlag,
  getUpsellSeenCookie,
  setFreeShippingIntentSessionFlag,
  updateAbandonedCartCookie,
  updateUpsellSeenCookie,
} from '../lib/cookieUtils.js'
import { checkUpsell, equal, serializeAbandonedCartForWhisper } from '../lib/cartUtils.js'
import { getCartFromPlatform } from '../lib/ecommerceCartFetchers.js'
import { formatCurrency } from '../lib/currencyUtils.js'
import { isSlideOutSurface } from '../templates/index.js'
 
/**
 * Abandoned cart + upsell + free-shipping messaging hook ported from v1.
 * The widget "trigger" mechanism is still implemented by sending a whisper
 * payload to BotMan, in the same string formats v1 used.
 */
export function useAbandonedCart({
  conf,
  agentId,
  openChat,
  whisper,
  onEventTrack,
}) {
  const resolveApiOrigin = useCallback(() => {
    const base = conf?.apiBaseUrl || conf?.api_base_url
    if (typeof base === 'string' && base.trim()) return base.trim().replace(/\/+$/, '')
    const fallback = conf?.fallbackEndpoint || conf?.chatServer || conf?.streamingEndpoint
    if (typeof fallback === 'string' && fallback.trim()) {
      try {
        return new URL(fallback, window.location.href).origin
      } catch {
        // ignore
      }
    }
    return ''
  }, [conf])

  // Upsell, abandoned-cart, and free-shipping triggers whisper into the
  // thread regardless of layout, but only force the panel open on a
  // slide-out surface - forcing an overlay/expand/floating panel open in
  // the background is far more intrusive than a drawer sliding in.
  const openChatIfSlideOut = useCallback(() => {
    if (isSlideOutSurface(conf)) openChat?.()
  }, [conf, openChat])

  const cartCheckInterval = useRef(null)
  const cartChangedRef = useRef(false)
  const freeShippingCheckedRef = useRef(false)
  const currentPageRef = useRef(typeof window !== 'undefined' ? window.location.pathname : '')
 
  const [freeShippingInlineMessage, setFreeShippingInlineMessage] = useState(false)
  const [freeShippingInlineMessageText, setFreeShippingInlineMessageText] = useState('')
 
  const getShopType = useCallback(() => {
    const shopType =
      conf?.shopType ||
      conf?.shop_type ||
      conf?.ecommerce_type?.toString?.() ||
      conf?.ometricsAI?.shopType ||
      conf?.ometricsAI?.ecommerce_type?.toString?.()
 
    if (shopType === '8' || shopType === 8) return 'shopify'
    if (shopType === '3' || shopType === 3) return 'bigCommerce'
    if (shopType === '13' || shopType === 13) return 'woocommerce'
    if (typeof shopType === 'string') {
      const normalized = shopType.toLowerCase()
      if (normalized.includes('magento')) return 'magento'
      if (normalized.includes('shopify')) return 'shopify'
      if (normalized.includes('bigcommerce') || normalized.includes('big_commerce'))
        return 'bigCommerce'
      if (normalized.includes('woocommerce') || normalized.includes('woo_commerce'))
        return 'woocommerce'
    }
    return shopType ? String(shopType).toLowerCase() : null
  }, [conf])
 
  const isCartPage = useCallback(() => {
    const currentPath = window.location.pathname || ''
    // BigCommerce Stencil uses /cart.php; Shopify/Woo use /cart, etc.
    return (
      /\/cart(?:\.php)?(?:\/|$)|\/basket|\/bag/i.test(currentPath) &&
      !/\/checkout/i.test(currentPath)
    )
  }, [])
 
  const isCheckoutPage = useCallback(() => {
    const currentPath = window.location.pathname
    return /\/checkout/i.test(currentPath)
  }, [])
 
  const hasUpsellBeenShown = useCallback(() => {
    if (!agentId) return false
    return getUpsellSeenCookie(agentId) === 1
  }, [agentId])
 
  const markUpsellAsShown = useCallback(() => {
    if (!agentId) return
    updateUpsellSeenCookie(agentId, 1)
  }, [agentId])
 
  const getUserId = useCallback(() => {
    return (
      conf?.uid ||
      conf?.sessionId ||
      conf?.userId ||
      conf?.user_id ||
      null
    )
  }, [conf])
 
  const checkUpsellMatch = useCallback(
    async (productId) => {
      if (!productId || !agentId) return false
      const userId = getUserId()
      if (!userId) return false
 
      const origin = resolveApiOrigin()
      if (!origin) return false
      const endpoint = `${origin}/Ochatbot/api/ometrics-ai/checkUpsell/${userId}/${agentId}`
      try {
        const formData = new FormData()
        formData.append('productId', productId)
        const response = await fetch(endpoint, { method: 'POST', body: formData })
        if (!response.ok) return false
        const data = await response.json()
        return data && typeof data.success !== 'undefined'
      } catch {
        return false
      }
    },
    [agentId, getUserId, resolveApiOrigin],
  )
 
  const triggerUpsell = useCallback(
    async (productId) => {
      if (!agentId || !productId) return false
      if (hasUpsellBeenShown()) return false
 
      const whisperData = `${agentId}ochatbot-upsell-${productId}`
      try {
        openChatIfSlideOut()
        whisper?.(whisperData)
        onEventTrack?.('upsellShown', {
          revenue: 0,
          transaction_id: String(productId),
        })
        markUpsellAsShown()
        return true
      } catch {
        return false
      }
    },
    [agentId, openChatIfSlideOut, whisper, onEventTrack, hasUpsellBeenShown, markUpsellAsShown],
  )
 
  const triggerAbandonedCart = useCallback(
    async (cartData, shopType) => {
      if (!agentId || !cartData || !shopType) return false
 
      const alreadyTriggered = getAbandonedCartCookie(agentId, 'abandonedCart')
      if (alreadyTriggered) return false
 
      const whisperData = `${agentId}ochatbot-abandoned-shopify-${btoa(
        JSON.stringify(serializeAbandonedCartForWhisper(cartData)),
      )}`
      try {
        openChatIfSlideOut()
        whisper?.(whisperData)

        onEventTrack?.('abandonedCart', { revenue: 0 })
        updateAbandonedCartCookie(agentId, 'abandonedCart', 1)
        return true
      } catch {
        return false
      }
    },
    [agentId, openChatIfSlideOut, whisper, onEventTrack],
  )
 
  const checkFreeShipping = useCallback(
    (currentCart, shopType, cartChanged) => {
      const currentPage = window.location.pathname
      const pageChanged = currentPage !== currentPageRef.current
      if (pageChanged) {
        freeShippingCheckedRef.current = false
        currentPageRef.current = currentPage
      }
      if (freeShippingCheckedRef.current && !cartChanged) return
      freeShippingCheckedRef.current = true
 
      setFreeShippingInlineMessage(false)
      setFreeShippingInlineMessageText('')
 
      // Builder/config may send booleans, 1/0, or "1"/"0"
      const flagOn = (val) =>
        val === true || val === 1 || val === '1' || val === 'true'

      if (!flagOn(conf?.free_shipping_message_enabled) || !currentCart) return
 
      const parseMoney = (val) => {
        if (val == null) return 0
        if (typeof val === 'number') return val
        if (typeof val === 'string') {
          const trimmed = val.trim()
          if (!trimmed) return 0
          const parsed = Number(trimmed)
          return Number.isFinite(parsed) ? parsed : 0
        }
        return 0
      }
 
      const itemsSubtotalRaw = parseMoney(
        currentCart.items_subtotal_price || currentCart.total_price || 0,
      )
      const thresholdRaw = parseMoney(conf?.free_shipping_threshold || 0)
      const lowThresholdRaw = parseMoney(conf?.free_low_threshold || 0)
 
      const isShopify = String(shopType || '').toLowerCase() === 'shopify'
      const normalizeThresholdToShopifyCents = (threshold) => {
        if (!Number.isFinite(threshold)) return 0
        if (!Number.isInteger(threshold)) return Math.round(threshold * 100)
        const cartLooksLikeCents = itemsSubtotalRaw >= 1000
        const thresholdLooksLikeDollars = threshold > 0 && threshold < 1000
        if (cartLooksLikeCents && thresholdLooksLikeDollars) return threshold * 100
        return threshold
      }
 
      const itemsSubtotalPrice = isShopify ? Math.round(itemsSubtotalRaw) : itemsSubtotalRaw
      const freeShippingThreshold = isShopify
        ? normalizeThresholdToShopifyCents(thresholdRaw)
        : thresholdRaw
      const freeLowThreshold = isShopify
        ? normalizeThresholdToShopifyCents(lowThresholdRaw)
        : lowThresholdRaw
      const isOnCartPageOnly = isCartPage()
 
      if (itemsSubtotalPrice >= freeShippingThreshold) {
        if (flagOn(conf?.show_free_shipping_message)) {
          if (flagOn(conf?.free_shipping_slideout) && conf?.free_shipping_slideout_text) {
            setFreeShippingInlineMessage(true)
            setFreeShippingInlineMessageText(conf.free_shipping_slideout_text)
          }
          if (
            flagOn(conf?.free_shipping_intent) &&
            conf?.free_shipping_intent_id &&
            // Every page load is a fresh JS context, so an in-memory guard
            // isn't enough: persist per browser session so the intent fires
            // once per session instead of on every page load.
            !getFreeShippingIntentSessionFlag(agentId)
          ) {
            setFreeShippingIntentSessionFlag(agentId, 1)
            // Match abandoned-cart / v1: open the panel before whispering
            // (slide-out surfaces only - see openChatIfSlideOut)
            openChatIfSlideOut()
            const whisperData = `intent-trigger-${conf.free_shipping_intent_id}`
            whisper?.(whisperData)
          }
          onEventTrack?.('freeShippingReachedSlideOut', { revenue: 0 })
        }
      } else if (
        itemsSubtotalPrice >= freeLowThreshold &&
        flagOn(conf?.show_shipping_progress)
      ) {
        const shouldShowProgress =
          cartChanged ||
          (isOnCartPageOnly && flagOn(conf?.show_shipping_progress_on_cart))
        if (shouldShowProgress) {
          const remaining = freeShippingThreshold - itemsSubtotalPrice
          const currencyInfo = currentCart.currency || null
          const remainingForDisplay = isShopify ? remaining / 100 : remaining
          const formattedRemaining = formatCurrency(remainingForDisplay, currencyInfo, conf)
 
          if (
            flagOn(conf?.free_shipping_progress_slideout) &&
            conf?.free_shipping_progress_slideout_text
          ) {
            const messageText = String(conf.free_shipping_progress_slideout_text).replace(
              '{remaining}',
              formattedRemaining,
            )
            setFreeShippingInlineMessage(true)
            setFreeShippingInlineMessageText(messageText)
          }
 
          if (
            flagOn(conf?.free_shipping_progress_intent) &&
            conf?.free_shipping_progress_intent_id
          ) {
            openChatIfSlideOut()
            const whisperData = `intent-trigger-${conf.free_shipping_progress_intent_id}__remaining::${formattedRemaining}`
            whisper?.(whisperData)
          }
 
          onEventTrack?.('freeShippingProgressSlideOut', { revenue: 0 })
        }
      }
    },
    [conf, agentId, onEventTrack, isCartPage, openChatIfSlideOut, whisper],
  )
 
  const checkAbandonedCart = useCallback(async () => {
    const runAbandonedCart = conf?.enable_abandoned_cart
    const runUpsell = conf?.enable_upsell
    const runFreeShipping = conf?.free_shipping_message_enabled
    if ((!runAbandonedCart && !runUpsell && !runFreeShipping) || !agentId) return
 
    try {
      const shopType = getShopType()
      if (!shopType) return
 
      const currentCart = await getCartFromPlatform(shopType, agentId)
      cartChangedRef.current = false
 
      const onCheckout = isCheckoutPage()
      const onCartOnly = isCartPage()
 
      if (onCheckout) {
        updateAbandonedCartCookie(agentId, 'abandonedCart', 0)
        localStorage.setItem(`ometricsCartDate${agentId}`, Date.now().toString())
        return
      }
 
      if (onCartOnly) {
        updateAbandonedCartCookie(agentId, 'abandonedCart', 0)
        localStorage.setItem(`ometricsCartDate${agentId}`, Date.now().toString())
        if (currentCart?.items?.length > 0) {
          checkFreeShipping(currentCart, shopType, false)
        }
        // keep going for upsell checks
      }
 
      const storedCartStr = localStorage.getItem(`ometricsCart${agentId}`)
      const storedCartDateStr = localStorage.getItem(`ometricsCartDate${agentId}`)
 
      const itemFound = Boolean(currentCart?.items && currentCart.items.length > 0)
      if (!currentCart || !itemFound) {
        if (storedCartStr) {
          localStorage.removeItem(`ometricsCart${agentId}`)
          localStorage.removeItem(`ometricsCartDate${agentId}`)
        }
        return
      }
 
      if (!storedCartStr || !storedCartDateStr) {
        cartChangedRef.current = true
        localStorage.setItem(`ometricsCart${agentId}`, JSON.stringify(currentCart))
        localStorage.setItem(`ometricsCartDate${agentId}`, Date.now().toString())
        updateAbandonedCartCookie(agentId, 'abandonedCart', 0)
 
        if (runUpsell && !hasUpsellBeenShown() && currentCart.items.length > 0) {
          const firstProductId = currentCart.items[0].product_id
          if (firstProductId) {
            const hasMatch = await checkUpsellMatch(firstProductId)
            if (hasMatch) {
              await triggerUpsell(firstProductId)
              return
            }
          }
        }
 
        checkFreeShipping(currentCart, shopType, true)
        return
      }
 
      if (shopType === 'woocommerce') {
        let currentCartDate
        if (/^\d+$/.test(storedCartDateStr)) {
          currentCartDate = Number.parseInt(storedCartDateStr, 10)
        } else {
          const dateObj = new Date(storedCartDateStr)
          currentCartDate = dateObj.getTime()
          if (Number.isNaN(currentCartDate)) return
        }
        const timeSinceUpdate = Date.now() - currentCartDate
        const abandonedTime = (conf.abandoned_time || 86400) * 1000
        if (timeSinceUpdate < abandonedTime) {
          updateAbandonedCartCookie(agentId, 'abandonedCart', 0)
          return
        }
        if (runAbandonedCart) await triggerAbandonedCart(currentCart, shopType)
        checkFreeShipping(currentCart, shopType, false)
        return
      }
 
      const storedCart = JSON.parse(storedCartStr)
      const storedCartDate = Number.parseInt(storedCartDateStr, 10)
      const cartsEqual = equal(currentCart, storedCart)
 
      if (!cartsEqual) {
        cartChangedRef.current = true
        localStorage.setItem(`ometricsCart${agentId}`, JSON.stringify(currentCart))
        localStorage.setItem(`ometricsCartDate${agentId}`, Date.now().toString())
        updateAbandonedCartCookie(agentId, 'abandonedCart', 0)
 
        if (
          runUpsell &&
          !hasUpsellBeenShown() &&
          storedCart?.items?.length > 0 &&
          currentCart?.items?.length > 0
        ) {
          const upsellProductId = checkUpsell(storedCart, currentCart)
          if (upsellProductId) {
            const hasMatch = await checkUpsellMatch(upsellProductId)
            if (hasMatch) {
              await triggerUpsell(upsellProductId)
              return
            }
          }
        }
 
        checkFreeShipping(currentCart, shopType, true)
        return
      }
 
      if (runAbandonedCart) {
        const timeSinceUpdate = Date.now() - storedCartDate
        const abandonedTime = (conf.abandoned_time || 86400) * 1000
        if (timeSinceUpdate > abandonedTime) {
          await triggerAbandonedCart(currentCart, shopType)
        }
      }
 
      checkFreeShipping(currentCart, shopType, false)
    } catch {
      // swallow (matches v1 behavior: trigger system should never break widget)
    }
  }, [
    conf,
    agentId,
    getShopType,
    isCheckoutPage,
    isCartPage,
    checkFreeShipping,
    triggerAbandonedCart,
    hasUpsellBeenShown,
    checkUpsellMatch,
    triggerUpsell,
  ])
 
  const startAbandonedCartMonitoring = useCallback(() => {
    const runAbandonedCart = conf?.enable_abandoned_cart
    const runUpsell = conf?.enable_upsell
    const runFreeShipping = conf?.free_shipping_message_enabled
    if ((!runAbandonedCart && !runUpsell && !runFreeShipping) || !agentId) return
 
    const pollingEnabled = conf?.enable_abandoned_cart_polling === true
 
    if (cartCheckInterval.current) {
      clearInterval(cartCheckInterval.current)
      cartCheckInterval.current = null
    }
 
    setTimeout(() => {
      checkAbandonedCart()
    }, 1000)
 
    if (pollingEnabled) {
      cartCheckInterval.current = setInterval(() => {
        checkAbandonedCart()
      }, 300000)
    }
  }, [conf, agentId, checkAbandonedCart])
 
  const stopAbandonedCartMonitoring = useCallback(() => {
    if (cartCheckInterval.current) {
      clearInterval(cartCheckInterval.current)
      cartCheckInterval.current = null
    }
  }, [])
 
  useEffect(() => {
    try {
      cleanupExpiredAbandonedCartFlags()
    } catch {
      // ignore
    }
    startAbandonedCartMonitoring()
    return () => stopAbandonedCartMonitoring()
  }, [startAbandonedCartMonitoring, stopAbandonedCartMonitoring])
 
  return {
    startAbandonedCartMonitoring,
    stopAbandonedCartMonitoring,
    checkAbandonedCart,
    freeShippingInlineMessage,
    freeShippingInlineMessageText,
  }
}

